import React, { useCallback, useEffect, useState } from 'react';

import '../../../verticals/caria/screens/saved.css';
import './qa-governance.css';
import {
  BottomNav,
  ColorRail,
  LockIcon,
  SELLER_NAV,
  TopBar,
} from '../../../commodity/components/caria';
import { useAuthChrome } from '../../identity-access/hooks/useAuthChrome';
import * as qa from '../api/qa-governance.api';

/**
 * QA Governance console — Week12 / PC-07.
 *
 * One read-mostly surface over the eight remediation packages. Its job is to
 * make the state of the release train's verification *visible*: how many epics
 * can actually be verified, which capabilities are out of scope and why, which
 * CI gates are real, which journeys the milestones ask for too early, and what
 * is still waiting on a named human signature.
 *
 * The write actions here are the recomputations and the periodic cuts — the
 * ones a machine may take. Freezing a threshold, signing a baseline or
 * approving an override need a person's name, so those show their blocked
 * state and the reason instead of a button that pretends otherwise.
 *
 * ADMIN-only: a non-admin gets 403 from every route and sees the locked state.
 */
type Tab =
  | 'padron'
  | 'alcance'
  | 'gates'
  | 'journeys'
  | 'legacy'
  | 'runbooks'
  | 'umbrales'
  | 'oraculos';

const TABS: { key: Tab; label: string; epic: string }[] = [
  { key: 'padron', label: 'Padrón de modos', epic: 'PC-07-E01' },
  { key: 'alcance', label: 'Alcance', epic: 'PC-07-E02' },
  { key: 'gates', label: 'Gates de CI', epic: 'PC-07-E03' },
  { key: 'journeys', label: 'Journeys E2E', epic: 'PC-07-E04' },
  { key: 'legacy', label: 'Paquetes legacy', epic: 'PC-07-E05' },
  { key: 'runbooks', label: 'Gate manual', epic: 'PC-07-E06' },
  { key: 'umbrales', label: 'Umbrales', epic: 'PC-07-E07' },
  { key: 'oraculos', label: 'Oráculos y línea base', epic: 'PC-07-E08' },
];

const modePill = (mode: string) => {
  if (mode === 'V-1') return 'ok';
  if (mode === 'V-2') return 'warn';
  if (mode === 'V-3') return 'mute';
  return 'bad';
};

/** Renders whatever the last action produced — success line or ERR code. */
const ActionResult: React.FC<{ result?: { ok: boolean; text: string; code?: string } }> = ({
  result,
}) =>
  result ? (
    <div className={`cia-qa-note${result.ok ? '' : ' error'}`} role="status">
      {result.code && <code>{result.code}</code>} {result.text}
    </div>
  ) : null;

export const QaGovernance: React.FC = () => {
  const authChrome = useAuthChrome();
  const [tab, setTab] = useState<Tab>('padron');
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string; code?: string } | undefined>();

  const [scope, setScope] = useState<qa.ScopeSummary | undefined>();
  const [epics, setEpics] = useState<qa.EpicVerification[]>([]);
  const [modeFilter, setModeFilter] = useState('');
  const [weekFilter, setWeekFilter] = useState('');

  const [scopeItems, setScopeItems] = useState<qa.ScopeItem[]>([]);
  const [coverage, setCoverage] = useState<Awaited<ReturnType<typeof qa.fetchCoverageCheck>>>();

  const [gates, setGates] = useState<qa.QualityGate[]>([]);
  const [gateDebt, setGateDebt] = useState<Awaited<ReturnType<typeof qa.fetchGateDebt>>>();
  const [envs, setEnvs] = useState<qa.EnvironmentSpec[]>([]);

  const [journeys, setJourneys] = useState<qa.Journey[]>([]);
  const [conflicts, setConflicts] = useState<qa.MilestoneConflict[]>([]);
  const [suiteWeek, setSuiteWeek] = useState(3);
  const [suite, setSuite] = useState<Awaited<ReturnType<typeof qa.fetchRegressionSuite>>>();

  const [packages, setPackages] = useState<qa.LegacyPackage[]>([]);
  const [e01, setE01] = useState<Awaited<ReturnType<typeof qa.fetchEntryCriterionE01>>>();

  const [runbooks, setRunbooks] = useState<qa.ManualRunbook[]>([]);
  const [manualCoverage, setManualCoverage] =
    useState<Awaited<ReturnType<typeof qa.fetchManualCoverage>>>();

  const [thresholds, setThresholds] = useState<qa.ThresholdDecision[]>([]);
  const [versions, setVersions] = useState<Record<string, qa.ThresholdVersion[]>>({});
  const [e03, setE03] = useState<Awaited<ReturnType<typeof qa.fetchEntryCriterionE03>>>();

  const [oracles, setOracles] = useState<qa.TestOracle[]>([]);
  const [usage, setUsage] = useState<Awaited<ReturnType<typeof qa.fetchOracleUsageCheck>>>();
  const [baselines, setBaselines] = useState<qa.TrackerBaseline[]>([]);

  /** Wraps an action so every failure lands in the same place with its ERR code. */
  const run = useCallback(async (label: string, fn: () => Promise<string>) => {
    setBusy(true);
    setResult(undefined);
    try {
      setResult({ ok: true, text: await fn() });
    } catch (err) {
      setResult({ ok: false, text: qa.messageOf(err), code: qa.errorCodeOf(err) });
      // eslint-disable-next-line no-console
      console.warn(`[qa-governance] ${label} falló`, err);
    } finally {
      setBusy(false);
    }
  }, []);

  const loadTab = useCallback(async (which: Tab) => {
    setLoading(true);
    setError(undefined);
    setForbidden(false);
    try {
      if (which === 'padron') {
        const [summary, list] = await Promise.all([
          qa.fetchVerificationScope(),
          qa.fetchVerificationModes(),
        ]);
        setScope(summary);
        setEpics(list);
      } else if (which === 'alcance') {
        const [items, check] = await Promise.all([qa.fetchScopeItems(), qa.fetchCoverageCheck()]);
        setScopeItems(items);
        setCoverage(check);
      } else if (which === 'gates') {
        const [list, debt, specs] = await Promise.all([
          qa.fetchQualityGates(),
          qa.fetchGateDebt(),
          qa.fetchEnvironmentSpecs(),
        ]);
        setGates(list);
        setGateDebt(debt);
        setEnvs(specs);
      } else if (which === 'journeys') {
        setJourneys(await qa.fetchJourneys());
      } else if (which === 'legacy') {
        const [list, status] = await Promise.all([
          qa.fetchLegacyPackages(),
          qa.fetchEntryCriterionE01(),
        ]);
        setPackages(list);
        setE01(status);
      } else if (which === 'runbooks') {
        const [list, cov] = await Promise.all([qa.fetchRunbooks(), qa.fetchManualCoverage()]);
        setRunbooks(list);
        setManualCoverage(cov);
      } else if (which === 'umbrales') {
        const [list, status] = await Promise.all([
          qa.fetchThresholds(),
          qa.fetchEntryCriterionE03(),
        ]);
        setThresholds(list);
        setE03(status);
        const byId: Record<string, qa.ThresholdVersion[]> = {};
        for (const t of list)
          byId[t.threshold_id] = await qa.fetchThresholdVersions(t.threshold_id);
        setVersions(byId);
      } else if (which === 'oraculos') {
        const [list, check, lines] = await Promise.all([
          qa.fetchOracles(),
          qa.fetchOracleUsageCheck(),
          qa.fetchBaselines(),
        ]);
        setOracles(list);
        setUsage(check);
        setBaselines(lines);
      }
    } catch (err) {
      const status = (err as { status?: number })?.status;
      if (status === 403 || /forbidden|insufficient permissions/i.test(qa.messageOf(err))) {
        setForbidden(true);
      } else {
        setError(qa.messageOf(err));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTab(tab);
  }, [tab, loadTab]);

  // The regression suite is a separate read: it changes with the week selector,
  // not with the tab, so folding it into loadTab would refetch every journey
  // each time the selector moves.
  useEffect(() => {
    if (tab !== 'journeys') return;
    qa.fetchRegressionSuite(suiteWeek)
      .then(setSuite)
      .catch(() => setSuite(undefined));
  }, [tab, suiteWeek]);

  const latestBaseline = baselines[baselines.length - 1];
  const visibleEpics = epics
    .filter((e) => (modeFilter ? e.verification_mode === modeFilter : true))
    .filter((e) => (weekFilter ? String(e.week) === weekFilter : true));

  return (
    <div className="cia-saved" data-testid="qa-governance">
      <TopBar {...authChrome} nav={SELLER_NAV} />
      <ColorRail />

      <div className="cia-saved-wrap">
        <div className="cia-saved-head">
          <h1>Gobierno de QA</h1>
        </div>
        <p className="cia-saved-sub">
          Week12 · PC-07 — remediación del Plan de Pruebas Maestro frente a lo realmente construido.
        </p>

        {scope && (
          <div className="cia-qa-counters" data-testid="qa-counters">
            <div className="cia-qa-counter">
              <b>{scope.v1}</b>
              <span>V-1 as-built</span>
              <small>X-01..X-07 completo</small>
            </div>
            <div className="cia-qa-counter">
              <b>{scope.v2}</b>
              <span>V-2 parcial</span>
              <small>+ diferimiento en la RTM</small>
            </div>
            <div className="cia-qa-counter">
              <b>{scope.v3}</b>
              <span>V-3 proceso manual</span>
              <small>runbook · evidencia · reversión</small>
            </div>
            <div className="cia-qa-counter">
              <b>{scope.v4}</b>
              <span>V-4 sin base</span>
              <small>no se diseñan casos</small>
            </div>
            <div className="cia-qa-counter accent">
              <b>
                {scope.alcance_verificable}/{scope.total_epicas}
              </b>
              <span>Alcance verificable</span>
              <small>denominador real de X-01</small>
            </div>
          </div>
        )}

        <div className="cia-qa-tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              role="tab"
              aria-selected={tab === t.key}
              className={tab === t.key ? 'on' : undefined}
              onClick={() => {
                setResult(undefined);
                setTab(t.key);
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading && (
          <div className="cia-saved-state" aria-busy="true">
            <p>Cargando…</p>
          </div>
        )}

        {!loading && forbidden && (
          <div className="cia-saved-state" data-testid="qa-governance-forbidden">
            <span className="cia-saved-state-ic">
              <LockIcon size={25} strokeWidth={1.8} />
            </span>
            <h2>Necesitas permisos de administración</h2>
            <p>La consola de gobierno de QA solo está disponible para cuentas ADMIN.</p>
          </div>
        )}

        {!loading && error && (
          <div className="cia-saved-state">
            <h2>No pudimos cargar la consola</h2>
            <p role="alert">{error}</p>
          </div>
        )}

        {!loading && !error && !forbidden && (
          <>
            <ActionResult result={result} />

            {/* ── PC-07-E01 ─────────────────────────────────────────────── */}
            {tab === 'padron' && (
              <>
                <div className="cia-qa-panel">
                  <h2>Padrón de modos de verificación · PC-07-E01</h2>
                  <p className="hint">
                    El modo se deriva del tracker: <code>gate = Manual</code> tiene precedencia
                    sobre el estado de construcción (BR-004). Las épicas V-4 no aportan ni numerador
                    ni denominador a X-01 (BR-006).
                  </p>

                  {scope && !scope.baseline_firmada && (
                    <div className="cia-qa-note blocked">
                      El padrón no puede publicarse: no hay línea base del tracker firmada por el
                      CTO (PC-07-E08 BR-008). Fírmala en la pestaña <b>Oráculos y línea base</b>.
                    </div>
                  )}
                  {scope && scope.baseline_firmada && !scope.baseline_vigente && (
                    <div className="cia-qa-note blocked">
                      La línea base firmada tiene más de 14 días: el padrón figura desactualizado
                      (PC-07-E08 BR-009).
                    </div>
                  )}

                  <div className="cia-qa-actions">
                    <button
                      className="cia-qa-btn"
                      disabled={busy || !latestBaseline}
                      title={
                        latestBaseline
                          ? undefined
                          : 'Abre la pestaña Oráculos y línea base para tomar un snapshot'
                      }
                      onClick={() =>
                        run('recalcular', async () => {
                          const r = await qa.recalculatePadron(latestBaseline!.baseline_id, true);
                          return `Simulación sobre ${latestBaseline!.baseline_id}: ${
                            r.cambiadas
                          } cambiarían, ${r.sin_cambio} sin cambio, ${
                            r.sin_clasificar
                          } sin clasificar.`;
                        })
                      }
                    >
                      Simular recálculo
                    </button>
                    <button
                      className="cia-qa-btn"
                      disabled={busy || !latestBaseline}
                      onClick={() =>
                        run('recalcular', async () => {
                          const r = await qa.recalculatePadron(latestBaseline!.baseline_id, false);
                          await loadTab('padron');
                          return `Padrón recalculado: ${r.cambiadas} épicas cambiaron de modo.`;
                        })
                      }
                    >
                      Recalcular padrón
                    </button>
                    <button
                      className="cia-qa-btn primary"
                      disabled={busy}
                      onClick={() =>
                        run('publicar', async () => {
                          await qa.publishPadron();
                          await loadTab('padron');
                          return 'Padrón publicado.';
                        })
                      }
                    >
                      Publicar padrón
                    </button>
                  </div>

                  <div className="cia-qa-filters">
                    <label>
                      Modo{' '}
                      <select value={modeFilter} onChange={(e) => setModeFilter(e.target.value)}>
                        <option value="">Todos</option>
                        {(['V-1', 'V-2', 'V-3', 'V-4'] as const).map((m) => (
                          <option key={m} value={m}>
                            {m} — {qa.MODE_LABEL[m]}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Semana{' '}
                      <select value={weekFilter} onChange={(e) => setWeekFilter(e.target.value)}>
                        <option value="">Todas</option>
                        {Array.from({ length: 11 }, (_, i) => i + 1).map((w) => (
                          <option key={w} value={String(w)}>
                            Week {w}
                          </option>
                        ))}
                      </select>
                    </label>
                    <span>{visibleEpics.length} épicas</span>
                  </div>

                  <div className="cia-qa-scroll">
                    <table className="cia-qa-table" data-testid="qa-padron-table">
                      <thead>
                        <tr>
                          <th>Épica</th>
                          <th>Semana</th>
                          <th>Tier</th>
                          <th>Construcción</th>
                          <th>Gate</th>
                          <th>Modo</th>
                          <th>Criterio de salida</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleEpics.map((e) => (
                          <tr key={e.epic_code}>
                            <td>
                              <code>{e.epic_code}</code>
                            </td>
                            <td>W{e.week}</td>
                            <td>{e.tier}</td>
                            <td>{e.build_status}</td>
                            <td>{e.gate}</td>
                            <td>
                              <span className={`cia-qa-pill ${modePill(e.verification_mode)}`}>
                                {e.verification_mode}
                              </span>
                            </td>
                            <td>{e.exit_criteria_set}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {/* ── PC-07-E02 ─────────────────────────────────────────────── */}
            {tab === 'alcance' && (
              <div className="cia-qa-panel">
                <h2>Rebaseline de alcance · PC-07-E02</h2>
                <p className="hint">
                  Ninguna capacidad sale del alcance sin dos señales de evidencia (estado en el
                  tracker + declaración de código) y una condición de reingreso objetiva.
                </p>

                {coverage && !coverage.puede_publicar && (
                  <div className="cia-qa-note blocked">
                    El plan v2.0 no puede publicarse: {coverage.huerfanas.length} capacidad(es)
                    citadas por el plan no figuran en el registro (BR-011).
                  </div>
                )}
                <div className="cia-qa-note blocked">
                  <b>DQ-002 abierta.</b> Suspender X-14 (cuadre del Payment Ledger) exige la
                  aprobación nominal de Dirección. Mientras no exista, el criterio queda vigente e
                  incumplible y se reporta como riesgo abierto en cada hito.
                </div>

                <div className="cia-qa-actions">
                  <button
                    className="cia-qa-btn"
                    disabled={busy}
                    onClick={() =>
                      run('corte semanal', async () => {
                        const r = await qa.runScopeReviewCycle();
                        await loadTab('alcance');
                        return `Corte semanal: ${r.reingresadas.length} reingresos, ${r.caducadas.length} exclusiones caducadas.`;
                      })
                    }
                  >
                    Ejecutar corte semanal
                  </button>
                </div>

                <div className="cia-qa-scroll">
                  <table className="cia-qa-table" data-testid="qa-scope-table">
                    <thead>
                      <tr>
                        <th>Capacidad</th>
                        <th>§ Plan</th>
                        <th>Épicas</th>
                        <th>Código</th>
                        <th>Decisión</th>
                        <th>Aprobó</th>
                        <th>Reingreso</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scopeItems.map((i) => (
                        <tr key={i.capability_id}>
                          <td>
                            <code>{i.capability_id}</code>
                            <br />
                            {i.capability_name}
                          </td>
                          <td>§{i.plan_section}</td>
                          <td>{i.related_epics.join(', ')}</td>
                          <td>{i.code_evidence}</td>
                          <td>
                            <span
                              className={`cia-qa-pill ${
                                i.scope_decision === 'En alcance' ? 'ok' : 'mute'
                              }`}
                            >
                              {i.scope_decision}
                            </span>
                            {i.affected_criteria?.length ? (
                              <>
                                <br />
                                <span className="cia-qa-pill warn">
                                  afecta {i.affected_criteria.join(', ')}
                                </span>
                              </>
                            ) : null}
                          </td>
                          <td>
                            {i.approved_by || <span className="cia-qa-pill warn">pendiente</span>}
                          </td>
                          <td>{i.reentry_condition}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── PC-07-E03 ─────────────────────────────────────────────── */}
            {tab === 'gates' && (
              <>
                <div className="cia-qa-panel">
                  <h2>Quality gates de CI · PC-07-E03</h2>
                  <p className="hint">
                    Un gate nace en <b>No implementado</b>; pasa a Advisory con evidencia de
                    ejecución y a Bloqueante tras 14 días estables. Un criterio de hito respaldado
                    por un gate Advisory se reporta &laquo;medido, no exigido&raquo;, nunca
                    cumplido.
                  </p>

                  {gateDebt && gateDebt.criterios_sin_respaldo.length > 0 && (
                    <div className="cia-qa-note blocked">
                      Criterios de hito sin gate bloqueante detrás:{' '}
                      <b>{gateDebt.criterios_sin_respaldo.join(', ')}</b> (BR-005).
                    </div>
                  )}

                  <div className="cia-qa-actions">
                    <button
                      className="cia-qa-btn"
                      disabled={busy}
                      onClick={() =>
                        run('deuda', async () => {
                          const r = await qa.reviewGateDebt();
                          await loadTab('gates');
                          return `Corte de deuda: ${r.marcados.length} gate(s) marcados En deuda.`;
                        })
                      }
                    >
                      Revisar deuda de gates
                    </button>
                  </div>

                  <div className="cia-qa-scroll">
                    <table className="cia-qa-table" data-testid="qa-gates-table">
                      <thead>
                        <tr>
                          <th>Gate</th>
                          <th>Herramienta</th>
                          <th>Workflow · job</th>
                          <th>Umbral</th>
                          <th>Modo</th>
                          <th>Criterio</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gates.map((g) => (
                          <tr key={g.gate_id}>
                            <td>
                              <code>{g.gate_id}</code>
                              <br />
                              {g.gate_name}
                            </td>
                            <td>{g.tool}</td>
                            <td>
                              <code>
                                {g.workflow_file.replace('.github/workflows/', '')} · {g.job_name}
                              </code>
                            </td>
                            <td>{g.threshold}</td>
                            <td>
                              <span
                                className={`cia-qa-pill ${
                                  g.mode === 'Bloqueante'
                                    ? 'ok'
                                    : g.mode === 'Advisory'
                                    ? 'warn'
                                    : g.mode === 'En deuda'
                                    ? 'bad'
                                    : 'mute'
                                }`}
                              >
                                {g.mode}
                              </span>
                            </td>
                            <td>{g.exit_criterion}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="cia-qa-panel">
                  <h2>Especificación de entornos</h2>
                  <p className="hint">
                    La plataforma real: DynamoDB, EventEmitter2 en proceso, GSIs y CloudWatch Logs.
                    El registro rechaza PostgreSQL, Kafka y Elasticsearch (VR-008).
                  </p>
                  <div className="cia-qa-scroll">
                    <table className="cia-qa-table">
                      <thead>
                        <tr>
                          <th>Entorno</th>
                          <th>Datos</th>
                          <th>Eventos</th>
                          <th>Búsqueda</th>
                          <th>Observabilidad</th>
                        </tr>
                      </thead>
                      <tbody>
                        {envs.map((e) => (
                          <tr key={e.environment}>
                            <td>
                              <b>{e.environment}</b>
                            </td>
                            <td>{e.motor_datos}</td>
                            <td>{e.bus_eventos}</td>
                            <td>{e.motor_busqueda}</td>
                            <td>{e.observabilidad}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {/* ── PC-07-E04 ─────────────────────────────────────────────── */}
            {tab === 'journeys' && (
              <div className="cia-qa-panel">
                <h2>Journeys E2E e hitos · PC-07-E04</h2>
                <p className="hint">
                  <code>earliest_executable_week</code> es derivada: el máximo de las semanas de las
                  épicas habilitantes. Un hito que exige un journey antes de esa semana es un
                  defecto de plan S2 para el Project Manager, no un defecto de producto.
                </p>

                <div className="cia-qa-actions">
                  <button
                    className="cia-qa-btn"
                    disabled={busy}
                    onClick={() =>
                      run('recalcular journeys', async () => {
                        const r = await qa.recalculateJourneys();
                        await loadTab('journeys');
                        return `${r.recalculados.length} journey(s) recalculados.`;
                      })
                    }
                  >
                    Recalcular journeys
                  </button>
                  <button
                    className="cia-qa-btn"
                    disabled={busy}
                    onClick={() =>
                      run('validar hitos', async () => {
                        const plan = journeys
                          .filter((j) => j.required_by_milestone && j.milestone_week)
                          .map((j) => ({
                            milestone: j.required_by_milestone as string,
                            week: j.milestone_week as number,
                            required_journeys: [j.journey_id],
                          }));
                        const r = await qa.validateMilestones(plan);
                        setConflicts(r.conflictos);
                        return r.valido
                          ? 'El cronograma no tiene contradicciones hito ↔ journey.'
                          : `${r.conflictos.length} contradicción(es); ${r.defectos.length} defecto(s) de plan S2 abiertos.`;
                      })
                    }
                  >
                    Validar hitos
                  </button>
                  <label className="cia-qa-filters" style={{ margin: 0 }}>
                    Suite de la semana{' '}
                    <select
                      value={suiteWeek}
                      onChange={(e) => setSuiteWeek(Number(e.target.value))}
                    >
                      {Array.from({ length: 11 }, (_, i) => i + 1).map((w) => (
                        <option key={w} value={w}>
                          Week {w}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                {suite && (
                  <div className="cia-qa-note" data-testid="qa-suite">
                    Suite de regresión núcleo de la semana {suite.semana}: <b>{suite.total}</b>{' '}
                    elemento(s) {suite.total > 0 ? `— ${suite.items.join(', ')}` : '— vacía'}
                  </div>
                )}

                {conflicts.length > 0 && (
                  <div className="cia-qa-note error" data-testid="qa-conflicts">
                    {conflicts.map((c) => (
                      <div key={`${c.milestone}-${c.journey_id}`}>
                        <b>{c.milestone}</b> (semana {c.milestone_week}) exige {c.journey_id},
                        ejecutable desde la semana {c.earliest_executable_week} por{' '}
                        {(c.epicas_determinantes ?? [c.epica_determinante]).map((epic, i, all) => (
                          <React.Fragment key={epic}>
                            <code>{epic}</code>
                            {i < all.length - 1 ? ' y ' : ''}
                          </React.Fragment>
                        ))}
                        .
                      </div>
                    ))}
                  </div>
                )}

                <div className="cia-qa-scroll">
                  <table className="cia-qa-table" data-testid="qa-journeys-table">
                    <thead>
                      <tr>
                        <th>Journey</th>
                        <th>Pasos</th>
                        <th>Ejecutable</th>
                        <th>Hito</th>
                        <th>Estado</th>
                        <th>Causa del bloqueo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {journeys.map((j) => (
                        <tr key={j.journey_id}>
                          <td>
                            <code>{j.journey_id}</code>
                            <br />
                            {j.journey_name}
                          </td>
                          <td>{j.steps?.length ?? 0}</td>
                          <td>W{j.earliest_executable_week}</td>
                          <td>
                            {j.required_by_milestone ? (
                              <span
                                className={`cia-qa-pill ${
                                  j.milestone_week && j.milestone_week < j.earliest_executable_week
                                    ? 'bad'
                                    : 'mute'
                                }`}
                              >
                                {j.required_by_milestone}
                                {j.milestone_week ? ` · W${j.milestone_week}` : ''}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>
                            <span
                              className={`cia-qa-pill ${j.status === 'Bloqueado' ? 'bad' : 'ok'}`}
                            >
                              {j.status}
                            </span>
                          </td>
                          <td>{j.bloqueo_causa}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── PC-07-E05 ─────────────────────────────────────────────── */}
            {tab === 'legacy' && (
              <div className="cia-qa-panel">
                <h2>Paquetes legacy Weeks 1–2 · PC-07-E05</h2>
                <p className="hint">
                  El criterio de entrada E-01 exige un Epic Package conforme antes de diseñar
                  pruebas. No hay migración parcial: el gate pasa a Ready for Build sólo con C1–C8
                  completos.
                </p>

                {e01 && (
                  <div className={`cia-qa-note${e01.fallan > 0 ? ' blocked' : ''}`}>
                    E-01: <b>{e01.cumplen}</b> de {e01.total_epicas} épicas cumplen.{' '}
                    {e01.fallan > 0 && <>Pendientes: {e01.pendientes.join(', ')}</>}
                  </div>
                )}

                <div className="cia-qa-scroll">
                  <table className="cia-qa-table" data-testid="qa-legacy-table">
                    <thead>
                      <tr>
                        <th>Paquete</th>
                        <th>Semana</th>
                        <th>Business Rules</th>
                        <th>05_QA</th>
                        <th>OpenAPI</th>
                        <th>Gate</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {packages.map((p) => (
                        <tr key={p.package_id}>
                          <td>
                            <code>{p.package_id}</code>
                          </td>
                          <td>W{p.week}</td>
                          <td>{p.business_rules_count ?? 0}</td>
                          <td>{p.qa_folder_complete ? 'completa' : 'incompleta'}</td>
                          <td>{p.openapi_status ?? 'No extraido'}</td>
                          <td>
                            <span
                              className={`cia-qa-pill ${
                                p.gate_status === 'Ready for Build' ? 'ok' : 'warn'
                              }`}
                            >
                              {p.gate_status}
                            </span>
                          </td>
                          <td>
                            <button
                              className="cia-qa-btn"
                              disabled={busy}
                              onClick={() =>
                                run('self-check', async () => {
                                  const r = await qa.runSelfCheck(p.package_id);
                                  await loadTab('legacy');
                                  const failed = r.criterios.filter((c) => c.resultado === 'Falla');
                                  return `${p.package_id} → ${r.gate_status}. Fallan: ${
                                    failed.map((c) => c.criterio.split(' ')[0]).join(', ') ||
                                    'ninguno'
                                  }.`;
                                })
                              }
                            >
                              Self-check
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── PC-07-E06 ─────────────────────────────────────────────── */}
            {tab === 'runbooks' && (
              <div className="cia-qa-panel">
                <h2>Cobertura del gate manual · PC-07-E06</h2>
                <p className="hint">
                  Para una épica V-3 el criterio de salida no es X-01..X-07: son las tres
                  condiciones — runbook ensayado por un tercero, evidencia auditable y
                  reversibilidad.
                </p>

                {manualCoverage && (
                  <div
                    className={`cia-qa-note${
                      manualCoverage.con_runbook_validado < manualCoverage.total_epicas_manual
                        ? ' blocked'
                        : ''
                    }`}
                    data-testid="qa-manual-coverage"
                  >
                    <b>{manualCoverage.con_runbook_validado}</b> de{' '}
                    <b>{manualCoverage.total_epicas_manual}</b> épicas con gate manual tienen
                    runbook validado. Sin runbook: {manualCoverage.sin_runbook.length}
                    {manualCoverage.caducados.length > 0 &&
                      ` · caducados: ${manualCoverage.caducados.length}`}
                    . Ninguna de ellas puede declararse verificada en un hito (BR-012).
                  </div>
                )}

                <div className="cia-qa-actions">
                  <button
                    className="cia-qa-btn"
                    disabled={busy}
                    onClick={() =>
                      run('caducidad', async () => {
                        const r = await qa.reviewRunbookExpiry();
                        await loadTab('runbooks');
                        return `${r.caducados.length} runbook(s) caducados por 90 días sin actividad.`;
                      })
                    }
                  >
                    Revisar caducidad
                  </button>
                </div>

                {runbooks.length === 0 ? (
                  <p className="hint">
                    No hay runbooks redactados todavía. Las{' '}
                    {manualCoverage?.total_epicas_manual ?? 23} épicas con gate manual (Weeks 5–6
                    más las 6 de Week 7 que el plan omitía) están listadas arriba como pendientes.
                  </p>
                ) : (
                  <div className="cia-qa-scroll">
                    <table className="cia-qa-table">
                      <thead>
                        <tr>
                          <th>Runbook</th>
                          <th>Épica</th>
                          <th>Proceso</th>
                          <th>Operador</th>
                          <th>Versión</th>
                          <th>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {runbooks.map((r) => (
                          <tr key={r.runbook_id}>
                            <td>
                              <code>{r.runbook_id}</code>
                            </td>
                            <td>
                              <code>{r.epic_code}</code>
                            </td>
                            <td>{r.process_name}</td>
                            <td>{r.operator_role}</td>
                            <td>{r.version}</td>
                            <td>
                              <span
                                className={`cia-qa-pill ${
                                  r.validation_status === 'Validado'
                                    ? 'ok'
                                    : r.validation_status === 'Caducado'
                                    ? 'bad'
                                    : 'warn'
                                }`}
                              >
                                {r.validation_status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ── PC-07-E07 ─────────────────────────────────────────────── */}
            {tab === 'umbrales' && (
              <div className="cia-qa-panel">
                <h2>Umbrales de negocio · PC-07-E07</h2>
                <p className="hint">
                  Un umbral en conflicto no tiene versión vigente, así que un caso de prueba no
                  puede declarar su resultado esperado. Congelarlo exige la aprobación nominal del
                  Product Owner y una verificación previa contra el código.
                </p>

                <div className="cia-qa-note blocked">
                  <b>DQ-001 abierta.</b> Si la regla de fotos congelada recoge las 6 categorías del
                  código o el código se relaja al mínimo de la especificación es una decisión del
                  Product Owner. Hasta entonces TH-001 permanece <b>En conflicto</b> y E-03 no se
                  cumple.
                </div>

                {e03 && (
                  <div className={`cia-qa-note${e03.cumple ? '' : ' blocked'}`}>
                    E-03: {e03.cumple ? 'cumplido' : 'no cumplido'}. En conflicto:{' '}
                    {e03.en_conflicto.join(', ') || '—'} · Discrepantes con código:{' '}
                    {e03.discrepantes.join(', ') || '—'}
                  </div>
                )}

                {thresholds.map((t) => (
                  <div key={t.threshold_id} style={{ marginTop: 14 }}>
                    <h2 style={{ fontSize: 14 }}>
                      <code>{t.threshold_id}</code> · {t.threshold_name}{' '}
                      <span className={`cia-qa-pill ${t.estado === 'Congelado' ? 'ok' : 'warn'}`}>
                        {t.estado}
                      </span>{' '}
                      <span className="cia-qa-pill mute">código: {t.code_alignment}</span>
                    </h2>
                    {t.active_provider && (
                      <p className="hint">
                        Proveedor activo: <b>{t.active_provider}</b> · objetivo declarado:{' '}
                        <b>{t.target_provider}</b>. QA prueba el activo (BR-004); la verificación
                        negativa debe ser por flujo, nunca por búsqueda de texto (BR-005).
                      </p>
                    )}
                    <div className="cia-qa-scroll">
                      <table className="cia-qa-table">
                        <thead>
                          <tr>
                            <th>Versión hallada</th>
                            <th>Fuente</th>
                            <th>Estado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(versions[t.threshold_id] ?? []).map((v) => (
                            <tr key={v.version_id}>
                              <td>{v.version_value}</td>
                              <td>
                                <code>{v.version_source}</code>
                              </td>
                              <td>
                                <span className="cia-qa-pill warn">{v.version_status}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── PC-07-E08 ─────────────────────────────────────────────── */}
            {tab === 'oraculos' && (
              <>
                <div className="cia-qa-panel">
                  <h2>Oráculos de prueba · PC-07-E08</h2>
                  <p className="hint">
                    Un caso no puede citar como fuente de resultado esperado un oráculo inexistente
                    o no verificado. Un oráculo que no aparece se sustituye con firma; no se
                    fabrica.
                  </p>

                  {usage && !usage.puede_disenar && (
                    <div className="cia-qa-note blocked" data-testid="qa-oracle-usage">
                      {usage.total} oráculo(s) no verificables bloquean el diseño de casos que los
                      citan: {usage.casos_con_oraculo_no_verificable.join(' · ')}
                    </div>
                  )}

                  <div className="cia-qa-scroll">
                    <table className="cia-qa-table" data-testid="qa-oracles-table">
                      <thead>
                        <tr>
                          <th>Oráculo</th>
                          <th>Tipo</th>
                          <th>Declarado en</th>
                          <th>Existencia</th>
                          <th>Ubicación / versión</th>
                          <th>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {oracles.map((o) => (
                          <tr key={o.oracle_id}>
                            <td>
                              <code>{o.oracle_id}</code>
                              <br />
                              {o.oracle_name}
                            </td>
                            <td>{o.oracle_type}</td>
                            <td>{o.declared_in}</td>
                            <td>
                              <span
                                className={`cia-qa-pill ${
                                  o.existence === 'Verificado' ? 'ok' : 'bad'
                                }`}
                              >
                                {o.existence}
                              </span>
                            </td>
                            <td>
                              {o.location} {o.version && `· v${o.version}`}
                            </td>
                            <td>{o.estado}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="cia-qa-panel">
                  <h2>Línea base del tracker</h2>
                  <p className="hint">
                    Sólo el CTO firma, y sólo con las cinco calificaciones cuestionadas de Week 2
                    (BC-01-E01…E05) reconciliadas con evidencia de código. Una firma caduca a los 14
                    días.
                  </p>

                  <div className="cia-qa-actions">
                    <button
                      className="cia-qa-btn"
                      disabled={busy}
                      onClick={() =>
                        run('snapshot', async () => {
                          const b = await qa.createBaseline();
                          await loadTab('oraculos');
                          return `Snapshot ${b.baseline_id} tomado sobre ${
                            b.filas
                          } filas (hash ${b.baseline_hash.slice(0, 12)}…).`;
                        })
                      }
                    >
                      Tomar snapshot
                    </button>
                    <button
                      className="cia-qa-btn"
                      disabled={busy || !latestBaseline}
                      onClick={() =>
                        run('reconciliar', async () => {
                          const id = latestBaseline!.baseline_id;
                          const evidence =
                            'backend/src/verticals/caria/vehicle-listings — PI-1 construyo draft → VIN → fotos → disclosure → activate y la maquina DRAFT→ACTIVE→PAUSED→ARCHIVED (ver docs/CARIA-WEEK2-RECONCILIATION-PLAN.md)';
                          for (const epic of [
                            'BC-01-E01',
                            'BC-01-E02',
                            'BC-01-E03',
                            'BC-01-E04',
                            'BC-01-E05',
                          ]) {
                            await qa.reconcileEpic(id, {
                              reconciled_epic: epic,
                              reconciled_status: 'Partial',
                              reconciliation_evidence: evidence,
                            });
                          }
                          await loadTab('oraculos');
                          return 'Las cinco calificaciones cuestionadas de Week 2 quedaron reconciliadas como Partial.';
                        })
                      }
                    >
                      Reconciliar Week 2 (5 épicas)
                    </button>
                    <button
                      className="cia-qa-btn primary"
                      disabled={busy || !latestBaseline}
                      onClick={() =>
                        run('firmar', async () => {
                          const firmante = window.prompt(
                            'Firma de la línea base — nombre y rol (p. ej. "CTO — Moises Breton"):',
                          );
                          if (!firmante) return 'Firma cancelada.';
                          await qa.signBaseline(latestBaseline!.baseline_id, firmante);
                          await loadTab('oraculos');
                          return `Línea base ${
                            latestBaseline!.baseline_id
                          } firmada por ${firmante}.`;
                        })
                      }
                    >
                      Firmar línea base (CTO)
                    </button>
                  </div>

                  <div className="cia-qa-scroll">
                    <table className="cia-qa-table" data-testid="qa-baselines-table">
                      <thead>
                        <tr>
                          <th>Línea base</th>
                          <th>Filas</th>
                          <th>Hash</th>
                          <th>Firmada por</th>
                          <th>Reconciliaciones pendientes</th>
                          <th>Vigencia</th>
                        </tr>
                      </thead>
                      <tbody>
                        {baselines.map((b) => (
                          <tr key={b.baseline_id}>
                            <td>
                              <code>{b.baseline_id}</code>
                            </td>
                            <td>{b.filas}</td>
                            <td>
                              <code>{b.baseline_hash.slice(0, 16)}…</code>
                            </td>
                            <td>
                              {b.baseline_signed_by || (
                                <span className="cia-qa-pill warn">sin firmar</span>
                              )}
                            </td>
                            <td>{b.reconciliaciones_pendientes}</td>
                            <td>
                              <span className={`cia-qa-pill ${b.vigente ? 'ok' : 'mute'}`}>
                                {b.vigente ? 'vigente' : 'no vigente'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default QaGovernance;
